export const FUEL_INTERVAL_MODES = {
  FILL_CLOSING: "fill_closing",
  CONSECUTIVE: "consecutive",
};

export const FUEL_GROUP_BY = {
  NONE: "none",
  OPERATOR: "operator",
  WORK_DAY: "work_day",
};

function parseHourMeter(value) {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseTimestamp(value) {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function averageOf(values) {
  const clean = values.filter((v) => v != null && Number.isFinite(v));
  if (!clean.length) return null;
  return clean.reduce((sum, v) => sum + v, 0) / clean.length;
}

function readHourMeter(record) {
  if (!record) return null;
  if (record.hour_meter != null && record.hour_meter !== "") {
    return record.hour_meter;
  }
  return record.hourMeter;
}

function readPanelHectares(record) {
  if (!record) return null;
  const raw =
    record.panel_hectares != null && record.panel_hectares !== ""
      ? record.panel_hectares
      : record.panelHectares != null && record.panelHectares !== ""
        ? record.panelHectares
        : record.hectares_painel;
  if (raw == null || raw === "") return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function parseTankFull(record) {
  if (!record) return null;
  if (record.tankFull === true || record.tankFull === 1) return true;
  if (record.tankFull === false || record.tankFull === 0) return false;
  const obs = record.observations || "";
  const match = obs.match(/tanque_cheio\s*=\s*([01])/i);
  if (match) return match[1] === "1";
  return null;
}

export function formatTankFullLabel(record, t) {
  const full = parseTankFull(record);
  if (full === true) return t("Cheio");
  if (full === false) return t("Parcial");
  return t("Não informado");
}

function dayKeyFromTimestamp(value) {
  const ms = parseTimestamp(value);
  if (ms == null) return "—";
  const date = new Date(ms);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function metricsFromDeltas(liters, hoursDelta, hectaresDelta) {
  const litersPerHour =
    hoursDelta != null && hoursDelta > 0 ? liters / hoursDelta : null;
  const litersPerHa =
    hectaresDelta != null && hectaresDelta > 0 ? liters / hectaresDelta : null;
  const haPerHour =
    hoursDelta != null && hoursDelta > 0 && hectaresDelta != null
      ? hectaresDelta / hoursDelta
      : null;

  return {
    hoursDelta,
    hectaresDelta,
    liters,
    litersPerHour,
    litersPerHa,
    haPerHour,
  };
}

function buildIntervalMetrics(previous, current, totalAreaHa, totalHours, litersOverride) {
  const liters =
    litersOverride != null ? litersOverride : Number(current.liters) || 0;
  const hmPrev = parseHourMeter(readHourMeter(previous));
  const hmCurr = parseHourMeter(readHourMeter(current));

  let hoursDelta = null;
  if (hmPrev != null && hmCurr != null && hmCurr > hmPrev) {
    hoursDelta = hmCurr - hmPrev;
  }

  let hectaresDelta = null;
  const haPrev = readPanelHectares(previous);
  const haCurr = readPanelHectares(current);
  if (haPrev != null && haCurr != null && haCurr > haPrev) {
    hectaresDelta = haCurr - haPrev;
  } else if (totalAreaHa > 0 && totalHours > 0 && hoursDelta != null && hoursDelta > 0) {
    hectaresDelta = totalAreaHa * (hoursDelta / totalHours);
  }

  return {
    ...metricsFromDeltas(liters, hoursDelta, hectaresDelta),
    hasHourMetrics: hoursDelta != null && hoursDelta > 0,
    hasHaMetrics: hectaresDelta != null && hectaresDelta > 0,
    hasInterval: previous != null && liters > 0,
  };
}

function groupRecordsByMachine(records) {
  const groups = {};
  (records || []).forEach((record) => {
    const key =
      record.equipmentId ||
      record.equipmentName ||
      record.machineIdent ||
      "—";
    if (!groups[key]) groups[key] = [];
    groups[key].push(record);
  });

  Object.keys(groups).forEach((key) => {
    groups[key].sort(
      (a, b) =>
        (parseTimestamp(a.timestamp) || 0) - (parseTimestamp(b.timestamp) || 0)
    );
  });
  return groups;
}

function collectConsecutiveIntervals(records, totalAreaHa, totalHours) {
  const groups = groupRecordsByMachine(records);
  const intervals = [];

  Object.values(groups).forEach((machineRecords) => {
    machineRecords.forEach((record, index) => {
      const previous = index > 0 ? machineRecords[index - 1] : null;
      intervals.push({
        ...buildIntervalMetrics(previous, record, totalAreaHa, totalHours),
        record,
        machineKey:
          record.equipmentId || record.equipmentName || record.machineIdent,
        timestamp: record.timestamp,
        intervalMode: FUEL_INTERVAL_MODES.CONSECUTIVE,
      });
    });
  });

  intervals.sort(
    (a, b) =>
      (parseTimestamp(a.timestamp) || 0) - (parseTimestamp(b.timestamp) || 0)
  );
  return intervals;
}

function collectFillClosingIntervals(records, totalAreaHa, totalHours) {
  const groups = groupRecordsByMachine(records);
  const intervals = [];

  Object.values(groups).forEach((machineRecords) => {
    let chainStart = null;
    let accumulatedLiters = 0;
    let partialCount = 0;

    machineRecords.forEach((record) => {
      const tankFull = parseTankFull(record);
      const liters = Number(record.liters) || 0;

      if (chainStart == null) {
        chainStart = record;
        accumulatedLiters = liters;
        partialCount = tankFull === true ? 0 : 1;
        if (tankFull === true) {
          chainStart = record;
          accumulatedLiters = 0;
          partialCount = 0;
        }
        return;
      }

      accumulatedLiters += liters;
      if (tankFull !== true) {
        partialCount += 1;
        return;
      }

      const metrics = buildIntervalMetrics(
        chainStart,
        record,
        totalAreaHa,
        totalHours,
        accumulatedLiters
      );

      intervals.push({
        ...metrics,
        record,
        machineKey:
          record.equipmentId || record.equipmentName || record.machineIdent,
        timestamp: record.timestamp,
        intervalMode: FUEL_INTERVAL_MODES.FILL_CLOSING,
        partialFillCount: partialCount,
        closingFill: true,
        hasInterval: metrics.hasInterval,
      });

      chainStart = record;
      accumulatedLiters = 0;
      partialCount = 0;
    });
  });

  intervals.sort(
    (a, b) =>
      (parseTimestamp(a.timestamp) || 0) - (parseTimestamp(b.timestamp) || 0)
  );
  return intervals;
}

function collectIntervals(records, totalAreaHa, totalHours, intervalMode) {
  if (intervalMode === FUEL_INTERVAL_MODES.FILL_CLOSING) {
    return collectFillClosingIntervals(records, totalAreaHa, totalHours);
  }
  return collectConsecutiveIntervals(records, totalAreaHa, totalHours);
}

function resolveTotalHours(records, totalAreaHa, totalHours, intervalMode) {
  const explicit = Number(totalHours) || 0;
  if (explicit > 0) return explicit;
  const withoutArea = collectIntervals(records, 0, 0, intervalMode);
  const fromMeters = withoutArea.reduce(
    (sum, item) => sum + (item.hoursDelta || 0),
    0
  );
  return fromMeters;
}

function summarizeIntervalBucket(intervals) {
  const last = intervals.length ? intervals[intervals.length - 1] : null;
  const last3 = intervals.slice(-3);

  function bucketFromList(list) {
    const partialCount = list.filter(
      (item) => (item.partialFillCount || 0) > 0
    ).length;
    return {
      liters: averageOf(list.map((i) => i.liters)),
      litersPerHour: averageOf(list.map((i) => i.litersPerHour)),
      litersPerHa: averageOf(list.map((i) => i.litersPerHa)),
      haPerHour: averageOf(list.map((i) => i.haPerHour)),
      stoppedPct: averageOf(list.map((i) => i.stoppedPct)),
      partialFillCount: partialCount,
      unreliableAverage: partialCount > 0,
    };
  }

  return {
    lastSupply: last
      ? {
          liters: last.liters,
          litersPerHour: last.litersPerHour,
          litersPerHa: last.litersPerHa,
          haPerHour: last.haPerHour,
          stoppedPct: last.stoppedPct,
          partialFillCount: last.partialFillCount || 0,
          unreliableAverage: (last.partialFillCount || 0) > 0,
        }
      : null,
    avgLast3: bucketFromList(last3),
    avgTotal: bucketFromList(intervals),
    intervalCount: intervals.length,
  };
}

function summarizeIntervals(validIntervals) {
  return summarizeIntervalBucket(validIntervals);
}

export function summarizeIntervalsByMachine(records, enrichOptions = {}) {
  const groups = groupRecordsByMachine(records);
  return Object.keys(groups)
    .sort((a, b) => a.localeCompare(b, "pt-BR"))
    .map((machineKey) => {
      const machineRecords = groups[machineKey];
      const enriched = enrichFuelingRecords(machineRecords, {
        ...enrichOptions,
        groupBy: FUEL_GROUP_BY.NONE,
      });
      const lastRecord = machineRecords[machineRecords.length - 1] || {};
      return {
        machineKey,
        machineLabel:
          lastRecord.equipmentName ||
          lastRecord.equipmentId ||
          machineKey,
        summary: enriched.summary,
        recordCount: machineRecords.length,
      };
    })
    .filter((item) => item.summary && item.summary.intervalCount > 0);
}

function fuelControlMatrixRows() {
  return [
    {
      id: "liters",
      labelKey: "Consumo (L)",
      format: (value) =>
        value != null && Number.isFinite(value)
          ? `${value.toFixed(1)} L`
          : "—",
      pick: (bucket) => bucket.liters,
    },
    {
      id: "lph",
      labelKey: "Combustível por hora",
      format: (value) => formatFuelIntervalRate(value, "L/h"),
      pick: (bucket) => bucket.litersPerHour,
    },
    {
      id: "lpha",
      labelKey: "Combustível por hectare",
      format: (value) => formatFuelIntervalRate(value, "L/ha"),
      pick: (bucket) => bucket.litersPerHa,
    },
    {
      id: "haph",
      labelKey: "Hectare por hora",
      format: (value) => formatHaPerHour(value),
      pick: (bucket) => bucket.haPerHour,
    },
    {
      id: "stopped",
      labelKey: "% parado no intervalo",
      format: (value) =>
        value != null && Number.isFinite(value)
          ? `${value.toFixed(1)} %`
          : "—",
      pick: (bucket) => bucket.stoppedPct,
    },
  ];
}

export function buildFuelControlMatrix(summary) {
  if (!summary || !summary.intervalCount) return null;

  const columns = [
    {
      id: "last",
      labelKey: "Último intervalo",
      bucket: summary.lastSupply,
    },
    {
      id: "last3",
      labelKey: "Média últimos 3",
      bucket: summary.avgLast3,
    },
    {
      id: "total",
      labelKey: "Média total",
      bucket: summary.avgTotal,
    },
  ].filter((col) => col.bucket);

  return { columns, rows: fuelControlMatrixRows() };
}

/** Matriz ilustrativa quando ainda não há abastecimentos no período. */
export function buildFuelControlExampleMatrix() {
  const makeBucket = (values, unreliableAverage) => ({
    liters: values.liters,
    litersPerHour: values.litersPerHour,
    litersPerHa: values.litersPerHa,
    haPerHour: values.haPerHour,
    stoppedPct: values.stoppedPct,
    partialFillCount: unreliableAverage ? 1 : 0,
    unreliableAverage: Boolean(unreliableAverage),
  });

  return {
    columns: [
      {
        id: "last",
        labelKey: "Último intervalo",
        bucket: makeBucket(
          {
            liters: 118.5,
            litersPerHour: 12.4,
            litersPerHa: 2.8,
            haPerHour: 4.4,
            stoppedPct: 8.5,
          },
          true
        ),
      },
      {
        id: "last3",
        labelKey: "Média últimos 3",
        bucket: makeBucket(
          {
            liters: 124.2,
            litersPerHour: 13.1,
            litersPerHa: 2.9,
            haPerHour: 4.5,
            stoppedPct: 7.2,
          },
          false
        ),
      },
      {
        id: "total",
        labelKey: "Média total",
        bucket: makeBucket(
          {
            liters: 121.0,
            litersPerHour: 12.8,
            litersPerHa: 2.85,
            haPerHour: 4.3,
            stoppedPct: 9.0,
          },
          false
        ),
      },
    ],
    rows: fuelControlMatrixRows(),
  };
}

export const FUEL_CONTROL_EXAMPLE_RECORDS = [
  {
    uuid: "example-fuel-1",
    timestamp: "2026-07-18T11:20:00Z",
    equipmentName: "100.005",
    operatorName: "Marcelo",
    liters: 45,
    hour_meter: 1520,
    panel_hectares: 1180.2,
    tankFull: false,
    intervalLitersPerHour: null,
    intervalLitersPerHa: null,
    intervalHaPerHour: null,
    hasFuelInterval: false,
    partialFillCount: 0,
    hasPanelPhoto: false,
    observations: "tanque_cheio=0 · abastecimento parcial",
    isExample: true,
  },
  {
    uuid: "example-fuel-2",
    timestamp: "2026-07-18T14:05:00Z",
    equipmentName: "100.005",
    operatorName: "Marcelo",
    liters: 118.5,
    hour_meter: 1529.5,
    panel_hectares: 1218.6,
    tankFull: true,
    intervalLitersPerHour: 12.4,
    intervalLitersPerHa: 2.8,
    intervalHaPerHour: 4.4,
    hasFuelInterval: true,
    partialFillCount: 1,
    hasPanelPhoto: true,
    observations: "tanque_cheio=1",
    isExample: true,
  },
  {
    uuid: "example-fuel-3",
    timestamp: "2026-07-17T16:40:00Z",
    equipmentName: "100.005",
    operatorName: "Marcelo",
    liters: 126,
    hour_meter: 1510,
    panel_hectares: 1165.0,
    tankFull: true,
    intervalLitersPerHour: 13.2,
    intervalLitersPerHa: 3.0,
    intervalHaPerHour: 4.6,
    hasFuelInterval: true,
    partialFillCount: 0,
    hasPanelPhoto: true,
    observations: "tanque_cheio=1",
    isExample: true,
  },
];

export function filterFuelingRecords(
  records,
  { dateFrom, dateTo, operatorId, machineIdent } = {}
) {
  let filtered = records || [];

  if (dateFrom) {
    const fromMs = parseDateBoundaryMs(dateFrom, "start");
    if (fromMs != null) {
      filtered = filtered.filter(
        (record) => (parseTimestamp(record.timestamp) || 0) >= fromMs
      );
    }
  }

  if (dateTo) {
    const toMs = parseDateBoundaryMs(dateTo, "end");
    if (toMs != null) {
      filtered = filtered.filter(
        (record) => (parseTimestamp(record.timestamp) || 0) <= toMs
      );
    }
  }

  if (operatorId != null && operatorId !== "") {
    const opId = String(operatorId);
    filtered = filtered.filter(
      (record) => String(record.operatorId || "") === opId
    );
  }

  if (machineIdent != null && machineIdent !== "") {
    const ident = String(machineIdent);
    filtered = filtered.filter(
      (record) =>
        String(record.equipmentId || record.equipmentName || "") === ident
    );
  }

  return filtered;
}

export function sumFuelingLiters(records) {
  return (records || []).reduce((sum, record) => {
    const liters = Number(record.liters);
    return sum + (Number.isFinite(liters) ? liters : 0);
  }, 0);
}

function parseDateBoundaryMs(value, boundary) {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const suffix = boundary === "end" ? "T23:59:59.999" : "T00:00:00.000";
    return parseTimestamp(`${value}${suffix}`);
  }
  return parseTimestamp(value);
}

export function listFuelOperators(records) {
  const map = {};
  (records || []).forEach((record) => {
    const id = record.operatorId;
    if (id == null || id === "") return;
    if (!map[id]) {
      map[id] = record.operatorName || String(id);
    }
  });
  return Object.keys(map)
    .map((id) => ({ id, name: map[id] }))
    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
}

export function listFuelMachines(records) {
  const map = {};
  (records || []).forEach((record) => {
    const ident = record.equipmentId || record.equipmentName;
    if (!ident) return;
    if (!map[ident]) {
      map[ident] = ident;
    }
  });
  return Object.keys(map)
    .map((ident) => ({ ident, name: ident }))
    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
}

function buildGroupedSummaries(records, enrichOptions) {
  const groupBy = enrichOptions.groupBy || FUEL_GROUP_BY.NONE;
  if (groupBy === FUEL_GROUP_BY.NONE) return [];

  const buckets = {};
  (records || []).forEach((record) => {
    let key;
    let label;
    if (groupBy === FUEL_GROUP_BY.OPERATOR) {
      key = String(record.operatorId || record.operatorName || "—");
      label = record.operatorName || key;
    } else {
      key = dayKeyFromTimestamp(record.timestamp);
      label = key;
    }
    if (!buckets[key]) {
      buckets[key] = { key, label, records: [] };
    }
    buckets[key].records.push(record);
  });

  return Object.values(buckets)
    .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"))
    .map((bucket) => {
      const enriched = enrichFuelingRecords(bucket.records, {
        ...enrichOptions,
        groupBy: FUEL_GROUP_BY.NONE,
      });
      return {
        key: bucket.key,
        label: bucket.label,
        summary: enriched.summary,
        recordCount: bucket.records.length,
      };
    });
}

/**
 * Enriquece abastecimentos com consumo L/h, L/ha e produção ha/h por intervalo.
 */
export function enrichFuelingRecords(
  records,
  {
    totalAreaHa = 0,
    totalHours = 0,
    intervalMode = FUEL_INTERVAL_MODES.FILL_CLOSING,
    groupBy = FUEL_GROUP_BY.NONE,
  } = {}
) {
  const area = Number(totalAreaHa) || 0;
  const hours = resolveTotalHours(records, area, totalHours, intervalMode);
  const intervals = collectIntervals(records, area, hours, intervalMode);
  const validIntervals = intervals.filter((item) => item.hasInterval);

  const byUuid = {};
  intervals.forEach((item) => {
    const uuid = item.record.uuid || item.record.timestamp;
    byUuid[uuid] = {
      intervalLitersPerHour: item.litersPerHour,
      intervalLitersPerHa: item.litersPerHa,
      intervalHaPerHour: item.haPerHour,
      intervalHours: item.hoursDelta,
      intervalHectares: item.hectaresDelta,
      intervalLiters: item.liters,
      hasFuelInterval: item.hasInterval,
      partialFillCount: item.partialFillCount || 0,
      closingFill: Boolean(item.closingFill),
    };
  });

  const defaultInterval = {
    intervalLitersPerHour: null,
    intervalLitersPerHa: null,
    intervalHaPerHour: null,
    intervalHours: null,
    intervalHectares: null,
    intervalLiters: null,
    hasFuelInterval: false,
    partialFillCount: 0,
    closingFill: false,
  };

  const rows = (records || []).map((record) => {
    const key = record.uuid || record.timestamp;
    return {
      ...record,
      tankFull: parseTankFull(record),
      ...(byUuid[key] || defaultInterval),
    };
  });

  const groupedSummaries = buildGroupedSummaries(records, {
    totalAreaHa: area,
    totalHours: hours,
    intervalMode,
    groupBy,
  });

  return {
    rows,
    summary: summarizeIntervals(validIntervals),
    groupedSummaries,
    totalAreaHa: area,
    totalHours: hours,
    intervalMode,
    groupBy,
  };
}

export function formatFuelIntervalRate(value, unit) {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(2)} ${unit}`;
}

export function formatHaPerHour(value) {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(2)} ha/h`;
}
